// components/ChatSystem/ChatBox.jsx
import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import ChatDisplay from './ChatDisplay';
import ChatInput from './ChatInput';
import MessageHover from './MessageHover';
import chatConfigService from '../services/chatConfigService';
import { useButtonMonitor } from '../../hooks/useButtonMonitor';
import { useQueryManager } from '../../hooks/useQueryManager';
import './ChatBox.css';

const ChatBox = ({
  pdfTitle = 'untitled',
  containerBounds = null,
  currentPage = 1 // Add this new prop
}) => {

  // =========================================================
  // 1. QUERY & CONFIG LOADING
  // =========================================================
  const loadQueryUsage = () => {
    try {
      const saved = sessionStorage.getItem(`chatbox_query_usage_${pdfTitle}`);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.warn('Failed to load query usage:', e);
      return null;
    }
  };

  const loadSavedMessages = () => {
    try {
      const saved = sessionStorage.getItem(`chatbox_messages_${pdfTitle}`);
      if (saved) {
        const messages = JSON.parse(saved);
        return messages.filter(msg =>
          msg && typeof msg === 'object' &&
          msg.text && msg.sender && msg.timestamp
        );
      }
    } catch (e) {
      console.warn('Failed to load chatbox messages:', e);
    }
    return [];
  };

  // =========================================================
  // 2. POSITION & SIZE STATE
  // =========================================================
  const [isDocked, setIsDocked] = useState(true);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const [size, setSize] = useState({
    width: Math.floor(window.innerWidth * (window.innerWidth < 768 ? 0.85 : 0.20)),
    height: Math.min(500, window.innerHeight * 0.6)
  });

  const [messages, setMessages] = useState(loadSavedMessages);
  const [sentText, setSentText] = useState('');
  const [showHover, setShowHover] = useState(false);
  const [queryUsage, setQueryUsage] = useState(loadQueryUsage);

  const [dropdownOpen, setDropdownOpen] = useState({
    queries: false,
    languages: false,
    models: false
  });
  const [isVisible, setIsVisible] = useState(true);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [isProcessingPending, setIsProcessingPending] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState('');

  // Refs for drag/resize calculations
  const chatboxRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const resizeStartPos = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });
  const prevBoundsRef = useRef(containerBounds);
  const isInitialMount = useRef(true);

  // =========================================================
  // 3. LOAD/SAVE CONFIG
  // =========================================================
  useEffect(() => {
    if (queryUsage) {
      sessionStorage.setItem(`chatbox_query_usage_${pdfTitle}`, JSON.stringify(queryUsage));
    }
  }, [queryUsage, pdfTitle]);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(`chatbox_config_${pdfTitle}`);
      if (saved) {
        const config = JSON.parse(saved);
        setIsDocked(config.isDocked);
        if (!config.isDocked) {
          setPosition(config.position);
          setSize(config.size);
        }
      }
    } catch (e) { console.warn("Load failed", e); }
  }, [pdfTitle]);

  useEffect(() => {
    const config = { isDocked, position, size };
    sessionStorage.setItem(`chatbox_config_${pdfTitle}`, JSON.stringify(config));
  }, [isDocked, position, size, pdfTitle]);

  // =========================================================
  // 4. ANTI-DRIFT & DOCKING LOGIC
  // =========================================================
  useLayoutEffect(() => {
    if (!containerBounds) return;

    if (isDocked) {
      const isMobile = containerBounds.width < 600;
      const targetW = Math.floor(containerBounds.width * (isMobile ? 0.8 : 0.20));

      setPosition({ x: 0, y: 0 });
      setSize({
        width: Math.max(300, targetW),
        height: containerBounds.height
      });
    }
    else if (prevBoundsRef.current) {
      const prev = prevBoundsRef.current;
      const curr = containerBounds;
      const deltaX = curr.left - prev.left;

      setPosition(prevPos => {
        let newX = prevPos.x - deltaX;
        let newY = prevPos.y;

        const maxX = curr.width - size.width;
        const maxY = curr.height - size.height;

        return {
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        };
      });

      setSize(prevSize => ({
        ...prevSize,
        height: Math.max(200, Math.min(prevSize.height, curr.height))
      }));
    }
    prevBoundsRef.current = containerBounds;
  }, [containerBounds, isDocked]);

  // =========================================================
  // 5. DATA LOADING & CHAT LOGIC
  // =========================================================

  const [languages, setLanguages] = useState({
    English: "English",
    Oromiffaa: "Oromiffaa",
    Tigrigna: "Tigrigna",
    Amharic: "Amharic",
    Italiano: "Italiano",
    Mandarin: "Mandarin",
    Hindi: "Hindi",
    Spanish: "Spanish",
    French: "French",
    Arabic: "Arabic",
    Bengali: "Bengali",
    Russian: "Russian",
    Portuguese: "Portuguese",
    Urdu: "Urdu",
    Indonesian: "Indonesian",
    German: "German",
    Japanese: "Japanese",
    Swahili: "Swahili",
    Marathi: "Marathi",
    Telugu: "Telugu",
    Turkish: "Turkish",
    Tamil: "Tamil",
    Vietnamese: "Vietnamese",
    Korean: "Korean",
    "Persian (Farsi)": "Persian (Farsi)",
    Kurdish: "Kurdish",
    Burmese: "Burmese",
    Afrikaans: "Afrikaans",
    Albanian: "Albanian",
    Armenian: "Armenian",
    Assamese: "Assamese",
    Azerbaijani: "Azerbaijani",
    Basque: "Basque",
    Belarusian: "Belarusian",
    Bosnian: "Bosnian",
    Bulgarian: "Bulgarian",
    Catalan: "Catalan",
    Croatian: "Croatian",
    Czech: "Czech",
    Danish: "Danish",
    Dutch: "Dutch",
    Estonian: "Estonian",
    "Filipino (Tagalog)": "Filipino (Tagalog)",
    Finnish: "Finnish",
    Galician: "Galician",
    Georgian: "Georgian",
    Greek: "Greek",
    Gujarati: "Gujarati",
    Hebrew: "Hebrew",
    Hungarian: "Hungarian",
    Icelandic: "Icelandic",
    Kannada: "Kannada",
    Kazakh: "Kazakh",
    Khmer: "Khmer",
    Lao: "Lao",
    Latvian: "Latvian",
    Lithuanian: "Lithuanian",
    Macedonian: "Macedonian",
    Malayalam: "Malayalam",
    Mongolian: "Mongolian",
    Nepali: "Nepali",
    Norwegian: "Norwegian",
    "Odia (Oriya)": "Odia (Oriya)",
    Pashto: "Pashto",
    Polish: "Polish",
    Punjabi: "Punjabi",
    Romanian: "Romanian",
    Serbian: "Serbian",
    Sinhala: "Sinhala",
    Slovak: "Slovak",
    Slovenian: "Slovenian",
    Swedish: "Swedish",
    Thai: "Thai",
    Ukrainian: "Ukrainian",
    Uzbek: "Uzbek",
    Zulu: "Zulu",
  });

  const [backendQueries, setBackendQueries] = useState([]);
  const [backendLLMs, setBackendLLMs] = useState({});

  const [selectedItems, setSelectedItems] = useState({
    language: 'english',
    llm: 'google-gemini',
    model: 'gemma-3-27b-it',
    query: null
  });

  const { sendButtonUpdate } = useButtonMonitor(setMessages);
  const {
    queries: customQueriesFromManager,
    selectedQuery: selectedQueryFromManager,
    editingQuery,
    handleQueryClick,
    clearEditingState,
    getQueryDisplayName
  } = useQueryManager(sendButtonUpdate, chatConfigService, selectedItems.query, setSelectedItems);

  useEffect(() => {
    try {
      sessionStorage.setItem(`chatbox_messages_${pdfTitle}`, JSON.stringify(messages));
    } catch (e) { console.warn('Failed to save chatbox messages:', e); }
  }, [messages, pdfTitle]);

  useEffect(() => {
    if (isInitialMount.current) {
      loadBackendData();
      loadUserState();
      isInitialMount.current = false;
    }
  }, []);

  useEffect(() => {
    if (isVisible && pendingMessages.length > 0 && !isProcessingPending) {
      processPendingMessages();
    }
  }, [isVisible, pendingMessages, isProcessingPending]);

  const processPendingMessages = async () => {
    if (isProcessingPending || pendingMessages.length === 0) return;
    setIsProcessingPending(true);
    for (const pendingMessage of [...pendingMessages]) {
      try {
        if (pendingMessage.type === 'pdftext') {
          await handlePdfTextFromQueue(pendingMessage.data);
        } else if (pendingMessage.type === 'chatmessage') {
          await handleChatMessageFromQueue(pendingMessage.data);
        }
      } catch (error) { console.error('❌ Failed to process pending message:', error); }
    }
    setPendingMessages([]);
    setIsProcessingPending(false);
  };

  const loadBackendData = async () => {
    try {
      setIsLoading(true);
      const queriesResponse = await chatConfigService.getQueries();
      if (queriesResponse.success && queriesResponse.queries) {
        setBackendQueries(queriesResponse.queries);
        if (!selectedItems.query && queriesResponse.queries.length > 0) {
          const defaultQuery = queriesResponse.queries.find(q => q.id === 'qa') || queriesResponse.queries[0];
          setSelectedItems(prev => ({ ...prev, query: defaultQuery.id }));
        }
      }
      const llmsResponse = await chatConfigService.getLLMs();
      if (llmsResponse.success && llmsResponse.llms) {
        const llmsData = {};
        llmsResponse.llms.forEach(llm => {
          llmsData[llm.id] = {
            name: llm.customName || llm.name,
            models: llm.supportedModels || [],
            defaultModel: llm.defaultModel,
            provider: llm.provider,
            description: llm.description
          };
        });
        setBackendLLMs(llmsData);
        if (llmsResponse.llms.length > 0) {
          const firstLLM = llmsResponse.llms[0];
          setSelectedItems(prev => ({
            ...prev,
            llm: firstLLM.id,
            model: firstLLM.defaultModel || (firstLLM.supportedModels?.[0]?.id)
          }));
        }
      }
    } catch (error) { console.error('❌ Failed to load backend data:', error); } finally { setIsLoading(false); }
  };

  const loadUserState = async () => {
    try {
      const stateResponse = await chatConfigService.getState();
      if (stateResponse.success && stateResponse.state) {
        setSelectedItems(prev => ({
          ...prev,
          language: stateResponse.state.language || prev.language,
          llm: stateResponse.state.llm || prev.llm,
          model: stateResponse.state.model || prev.model,
          query: stateResponse.state.query || prev.query
        }));
      }
    } catch (error) { console.error('❌ Failed to load user state:', error); }
  };

  // Event Listeners (System & External)
  useEffect(() => {
    const handleSystemMessage = (event) => {
      const { message, type, document } = event.detail;
      if (type === 'pdf_welcome' || message?.source === 'pdf_welcome') {
        setMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          text: message.text || message,
          sender: 'bot',
          timestamp: new Date().toISOString(),
          source: 'pdf_welcome',
          document: document || message.document
        }]);
      } else {
        const text = message?.text || (typeof message === 'string' ? message : null);
        if (text) {
          setMessages(prev => [...prev, {
            id: Date.now() + Math.random(),
            text: text,
            sender: 'system',
            timestamp: new Date().toISOString(),
            source: message?.source || type || 'system',
            systemType: message?.systemType || 'info'
          }]);
        }
      }
    };
    window.addEventListener('systemMessage', handleSystemMessage);
    return () => window.removeEventListener('systemMessage', handleSystemMessage);
  }, []);

  // ************************************************************
  // UPDATED: External Message Handler with Page Number
  // ************************************************************
  useEffect(() => {
    const handleExternalMessage = (event) => {
      if (event.detail && event.detail.contextData) {
        const { selectedText, contextBefore, contextAfter } = event.detail.contextData;
        const pdfMessage = {
          id: Date.now(),
          text: selectedText,
          sender: 'user',
          timestamp: new Date().toISOString(),
          source: 'pdftext',
          contextData: { selectedText, contextBefore, contextAfter },
          displayFormat: 'highlighted',
          pageNumber: currentPage
        };
        const systemMessage = {
          id: Date.now() - 1,
          text: `📖 Analyzing the selected text (Page ${currentPage}):`,
          sender: 'system',
          systemType: 'info',
          timestamp: new Date().toISOString(),
          pageNumber: currentPage
        };

        if (isVisible) {
          setMessages(prev => [...prev, systemMessage, pdfMessage]);
          handleSendPdfText(pdfMessage);
        } else {
          addToPendingMessages('pdftext', { pdfMessage, systemMessage });
          setIsVisible(true);
        }
      } else if (event.detail && event.detail.text) {
        const userMessage = {
          id: Date.now(),
          text: event.detail.text,
          sender: 'user',
          timestamp: new Date().toISOString(),
          source: 'chatinput',
          pageNumber: currentPage
        };
        if (isVisible) {
          setMessages(prev => [...prev, userMessage]);
          handleSendMessage(event.detail.text);
        } else {
          addToPendingMessages('chatmessage', { text: event.detail.text, userMessage });
          setIsVisible(true);
        }
      }
    };
    window.addEventListener('sendToChat', handleExternalMessage);
    return () => window.removeEventListener('sendToChat', handleExternalMessage);
  }, [pdfTitle, isVisible, currentPage]);

  const addToPendingMessages = (type, data) => {
    setPendingMessages(prev => [...prev, { type, data, timestamp: Date.now() }]);
  };

  const handlePdfTextFromQueue = async ({ pdfMessage, systemMessage }) => {
    setMessages(prev => [...prev, systemMessage, pdfMessage]);
    await handleSendPdfText(pdfMessage);
  };

  const handleChatMessageFromQueue = async ({ text, userMessage }) => {
    setMessages(prev => [...prev, userMessage]);
    await handleSendMessage(text);
  };

  const clearConversationHistory = () => {
    if (window.confirm('Are you sure you want to clear the conversation history? This will remove all messages from this session.')) {
      setMessages([]);
      try {
        sessionStorage.removeItem(`chatbox_messages_${pdfTitle}`);
      } catch (e) { console.warn('Failed to clear conversation history:', e); }
    }
  };

  // ************************************************************
  // UPDATED HANDLER: Include currentPage in PDF text requests
  // ************************************************************
  const handleSendPdfText = useCallback(async (pdfMessage) => {
    setIsLoading(true);
    try {
      const response = await chatConfigService.sendPdfText(
        pdfMessage.contextData.selectedText,
        pdfMessage.contextData.contextBefore,
        pdfMessage.contextData.contextAfter,
        {
          selectedLanguage: selectedItems.language,
          selectedLlm: selectedItems.llm,
          selectedModel: selectedItems.model,
          selectedQuery: selectedItems.query,
          pdfTitle: pdfTitle,
          currentPage: pdfMessage.pageNumber || currentPage
        }
      );

      const structuredData = response.processed?.structured || {};

      const botMessage = {
        id: Date.now() + 1,
        text: structuredData.content || response.processed?.text || response.chatResponse || response.message,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        pageNumber: currentPage,
        sourcePage: pdfMessage.pageNumber || currentPage,

        formatting: structuredData.formatting || null,
        summary: structuredData.summary || null,
        isFormatted: !!structuredData.formatting,

        apiResponse: response
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        text: "Sorry, I'm having trouble processing the PDF text right now. Please try again.",
        sender: 'bot',
        timestamp: new Date().toISOString(),
        pageNumber: currentPage
      }]);
    } finally { setIsLoading(false); }
  }, [selectedItems, pdfTitle, currentPage]);

  // ************************************************************
  // UPDATED HANDLER: Include currentPage in chat message requests
  // ************************************************************
  const handleSendMessage = useCallback(async (text) => {
    if (!text || text.trim() === '') return;

    const userMessage = {
      id: Date.now(),
      text: text.trim(),
      sender: 'user',
      timestamp: new Date().toISOString(),
      source: 'chatinput',
      pageNumber: currentPage
    };

    setMessages(prev => [...prev, userMessage]);
    setSentText(text.trim());
    setShowHover(true);
    setIsLoading(true);
    setTimeout(() => setShowHover(false), 2000);

    try {
      const response = await chatConfigService.sendChatMessage(text, {
        selectedLanguage: selectedItems.language,
        selectedLlm: selectedItems.llm,
        selectedModel: selectedItems.model,
        selectedQuery: selectedItems.query,
        source: 'chatinput',
        pdfTitle: pdfTitle,
        currentPage: currentPage
      });

      const structuredData = response.processed?.structured || {};

      const botMessage = {
        id: Date.now() + 1,
        text: structuredData.content || response.processed?.text || response.chatResponse || response.message || `I received: "${text.substring(0, 50)}..."`,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        pageNumber: currentPage,
        sourcePage: currentPage,

        formatting: structuredData.formatting || null,
        summary: structuredData.summary || null,
        isFormatted: !!structuredData.formatting,

        apiResponse: response
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        text: "Sorry, I'm having trouble connecting to the chat service right now. Please try again.",
        sender: 'bot',
        timestamp: new Date().toISOString(),
        pageNumber: currentPage
      }]);
    } finally { setIsLoading(false); }
  }, [selectedItems, pdfTitle, currentPage]);

  // =========================================================
  // 6. HELPER FUNCTIONS
  // =========================================================
  const getAllQueries = () => {
    const seenIds = new Set();
    const combined = [];
    const usage = queryUsage;

    backendQueries.forEach(query => {
      if (!seenIds.has(query.id)) {
        seenIds.add(query.id);
        combined.push({ ...query, isCustom: query.isCustom || false, lastUsed: usage?.[query.id] || null });
      }
    });

    // Handle Custom Queries from hook
    // 'customQueriesFromManager' is an array of entries [ [key, val], ... ] due to Object.entries in earlier version
    // BUT in the 'useQueryManager' hook provided earlier, 'queries' returns the ordered array [ [key, val], [key, val] ]
    // Let's verify structure: hook returns { queries: getOrderedQueries() } where getOrderedQueries returns entries.
    customQueriesFromManager.forEach(([key, query]) => {
      if (query.isCustom && !seenIds.has(key)) {
        seenIds.add(key);
        combined.push({
          id: key,
          name: query.name,
          text: query.text,
          type: 'custom',
          isCustom: true,
          isEmpty: query.isEmpty,
          category: 'custom',
          lastUsed: usage?.[key] || null
        });
      }
    });

    if (!usage) {
      combined.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    } else {
      combined.sort((a, b) => {
        if (!a.lastUsed && !b.lastUsed) return a.name.localeCompare(b.name);
        if (!a.lastUsed) return 1;
        if (!b.lastUsed) return -1;
        return new Date(b.lastUsed) - new Date(a.lastUsed);
      });
    }
    return combined;
  };

  const handleQueryAction = useCallback((queryId, event) => {
    const now = new Date().toISOString();
    setQueryUsage(prev => ({ ...(prev || {}), [queryId]: now }));
    setSelectedItems(prev => ({ ...prev, query: queryId }));

    // We need to look up the query to know if it is custom
    const allQueries = getAllQueries();
    const query = allQueries.find(q => q.id === queryId);
    if (!query) return;

    if (query.isCustom) {
      handleQueryClick(queryId, event);
    } else {
      sendButtonUpdate('query_changed', { queryId, queryKey: queryId });
    }
  }, [handleQueryClick, sendButtonUpdate, backendQueries, customQueriesFromManager]);

  const handleLanguageSelect = useCallback((key, name) => {
    setSelectedItems(prev => ({ ...prev, language: key }));
    setDropdownOpen(prev => ({ ...prev, languages: false }));
    sendButtonUpdate('language_changed', { languageName: name });
  }, [sendButtonUpdate]);

  const handleModelSelect = useCallback((id, name) => {
    setSelectedItems(prev => ({ ...prev, model: id }));
    setDropdownOpen(prev => ({ ...prev, models: false }));
    sendButtonUpdate('model_changed', { modelId: id, modelName: name });
  }, [sendButtonUpdate]);

  const toggleDropdown = useCallback((type) => {
    setDropdownOpen(prev => {
      if (prev[type]) {
        return { queries: false, languages: false, models: false };
      }
      return {
        queries: false,
        languages: false,
        models: false,
        [type]: true
      };
    });
  }, []);

  const handleVoiceInput = useCallback(() => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      text: "Voice input activated - feature coming soon!",
      sender: 'system',
      timestamp: new Date().toISOString()
    }]);
  }, []);

  const handleClose = useCallback(() => setIsVisible(false), []);

  // =========================================================
  // 7. DRAG & RESIZE HANDLERS (UPDATED FOR TOUCH)
  // =========================================================

  const getClientPos = (e) => {
    const source = e.touches ? e.touches[0] : e;
    return {
      clientX: source.clientX,
      clientY: source.clientY
    };
  };

  const handleDragStart = useCallback((e) => {
    if (e.target.closest('.header-buttons') || e.target.closest('.close-button')) return;

    if (isDocked) setIsDocked(false);
    setIsDragging(true);

    const { clientX, clientY } = getClientPos(e);
    const boxRect = chatboxRef.current.getBoundingClientRect();

    dragStartRef.current = {
      x: clientX - boxRect.left,
      y: clientY - boxRect.top
    };
  }, [isDocked]);

  const handleResizeStart = useCallback((direction, e) => {
    e.stopPropagation();
    if (isDocked) setIsDocked(false);

    setIsResizing(true);
    setResizeDirection(direction);

    const { clientX, clientY } = getClientPos(e);

    resizeStartPos.current = {
      x: clientX,
      y: clientY,
      width: size.width,
      height: size.height,
      left: position.x,
      top: position.y
    };
  }, [isDocked, size, position]);

  const handleGlobalMove = useCallback((e) => {
    if (!containerBounds) return;

    if (e.cancelable && (isDragging || isResizing) && e.type === 'touchmove') {
      e.preventDefault();
    }

    const { clientX, clientY } = getClientPos(e);

    if (isDragging) {
      let newX = clientX - containerBounds.left - dragStartRef.current.x;
      let newY = clientY - containerBounds.top - dragStartRef.current.y;

      const maxX = containerBounds.width - size.width;
      const maxY = containerBounds.height - size.height;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }

    if (isResizing) {
      const deltaX = clientX - resizeStartPos.current.x;
      const deltaY = clientY - resizeStartPos.current.y;

      let newWidth = resizeStartPos.current.width;
      let newHeight = resizeStartPos.current.height;
      let newX = resizeStartPos.current.left;
      let newY = resizeStartPos.current.top;

      switch (resizeDirection) {
        case 'right':
          newWidth = Math.max(300, resizeStartPos.current.width + deltaX);
          break;
        case 'left':
          newWidth = Math.max(300, resizeStartPos.current.width - deltaX);
          newX = resizeStartPos.current.left + deltaX;
          break;
        case 'bottom':
          newHeight = Math.max(200, resizeStartPos.current.height + deltaY);
          break;
        case 'top':
          newHeight = Math.max(200, resizeStartPos.current.height - deltaY);
          newY = resizeStartPos.current.top + deltaY;
          break;
        case 'bottom-right':
          newWidth = Math.max(300, resizeStartPos.current.width + deltaX);
          newHeight = Math.max(200, resizeStartPos.current.height + deltaY);
          break;
        case 'bottom-left':
          newWidth = Math.max(300, resizeStartPos.current.width - deltaX);
          newHeight = Math.max(200, resizeStartPos.current.height + deltaY);
          newX = resizeStartPos.current.left + deltaX;
          break;
        case 'top-right':
          newWidth = Math.max(300, resizeStartPos.current.width + deltaX);
          newHeight = Math.max(200, resizeStartPos.current.height - deltaY);
          newY = resizeStartPos.current.top + deltaY;
          break;
        case 'top-left':
          newWidth = Math.max(300, resizeStartPos.current.width - deltaX);
          newHeight = Math.max(200, resizeStartPos.current.height - deltaY);
          newX = resizeStartPos.current.left + deltaX;
          newY = resizeStartPos.current.top + deltaY;
          break;
        default: break;
      }

      const maxWidth = containerBounds.width - newX;
      const maxHeight = containerBounds.height - newY;

      newWidth = Math.min(newWidth, maxWidth);
      newHeight = Math.min(newHeight, maxHeight);

      newWidth = Math.max(300, newWidth);
      newHeight = Math.max(200, newHeight);
      newX = Math.max(0, Math.min(newX, containerBounds.width - newWidth));
      newY = Math.max(0, Math.min(newY, containerBounds.height - newHeight));

      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, isResizing, containerBounds, size, resizeDirection]);

  const handleGlobalEnd = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection('');
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleGlobalMove);
      window.addEventListener('mouseup', handleGlobalEnd);
      window.addEventListener('touchmove', handleGlobalMove, { passive: false });
      window.addEventListener('touchend', handleGlobalEnd);

      return () => {
        window.removeEventListener('mousemove', handleGlobalMove);
        window.removeEventListener('mouseup', handleGlobalEnd);
        window.removeEventListener('touchmove', handleGlobalMove);
        window.removeEventListener('touchend', handleGlobalEnd);
      };
    }
  }, [isDragging, isResizing, handleGlobalMove, handleGlobalEnd]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-container')) {
        setDropdownOpen({ queries: false, languages: false, models: false });
        clearEditingState();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [clearEditingState]);

  // =========================================================
  // 8. RENDER
  // =========================================================

  if (!containerBounds || !isVisible) return null;

  return (
    <div className={`chatbox-container ${isDocked ? 'chatbox-docked' : 'chatbox-floating'}`}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 50
      }}
    >
      <div
        ref={chatboxRef}
        className="chatbox"
        style={{
          position: 'absolute',
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${size.width}px`,
          height: `${size.height}px`,
          pointerEvents: 'auto'
        }}
      >
        {/* Header */}
        <div
          className="chatbox-header"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <div className="header-buttons">

            {/* Query Button */}
            <div className="dropdown-container">
              <button
                className="header-button red"
                onClick={(e) => { e.stopPropagation(); toggleDropdown('queries'); }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                Queries
              </button>
              {dropdownOpen.queries && (
                <div className="dropdown-menu queries-menu">
                  {getAllQueries().map((query) => (
                    <div key={`query-${query.id}`} className="dropdown-item-with-indicator">
                      <div className={`selection-indicator ${selectedItems.query === query.id ? 'selected' : ''} ${query.isEmpty ? 'empty' : ''} ${query.isCustom ? 'custom' : 'predefined'}`} />
                      <button
                        className={`dropdown-item ${query.isCustom ? 'custom-query' : 'predefined-query'}`}
                        onClick={(e) => { e.stopPropagation(); handleQueryAction(query.id, e); }}
                        onDoubleClick={(e) => {
                          if (query.isCustom && !query.isEmpty) {
                            e.stopPropagation();
                            handleQueryAction(query.id, e);
                          }
                        }}
                        style={{ flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: '2px' }}
                      >
                        <span className="query-name" style={{ fontWeight: '500', lineHeight: '1.2' }}>
                          {query.name}
                        </span>

                        {query.isCustom && (
                          <span className="query-subtitle" style={{ fontSize: '0.7rem', opacity: 0.65, fontStyle: 'italic', lineHeight: '1' }}>
                            {query.isEmpty ? "Click to create" : "Double-click to edit"}
                          </span>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Language Button */}
            <div className="dropdown-container">
              <button
                className="header-button red"
                onClick={(e) => { e.stopPropagation(); toggleDropdown('languages'); }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                Languages
              </button>
              {dropdownOpen.languages && (
                <div className="dropdown-menu">
                  {Object.entries(languages).map(([key, name]) => (
                    <div key={`lang-${key}`} className="dropdown-item-with-indicator">
                      <div className={`selection-indicator ${selectedItems.language === key ? 'selected' : ''}`} />
                      <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleLanguageSelect(key, name); }}>
                        {name}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Model Button */}
            <div className="dropdown-container">
              <button
                className="header-button red"
                onClick={(e) => { e.stopPropagation(); toggleDropdown('models'); }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                Models
              </button>
              {dropdownOpen.models && (
                <div className="dropdown-menu models-menu">
                  {(backendLLMs[selectedItems.llm]?.models || []).map((model) => (
                    <div key={`model-${model.id}`} className="dropdown-item-with-indicator">
                      <div className={`selection-indicator ${selectedItems.model === model.id ? 'selected' : ''}`} />
                      <button className="dropdown-item model-item" onClick={(e) => { e.stopPropagation(); handleModelSelect(model.id, model.name); }}>
                        <span className="model-name">{model.name}</span>
                        {model.maxTokens && <span className="model-tokens">({Math.floor(model.maxTokens / 1000)}K tokens)</span>}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Msg Indicator */}
            {pendingMessages.length > 0 && (
              <button
                className="header-button pending-indicator"
                title={`${pendingMessages.length} pending message(s)`}
                onClick={() => setIsVisible(true)}
              >
                ⚡ {pendingMessages.length}
              </button>
            )}
          </div>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        {/* Chat Content */}
        <div className="chat-content">
          <ChatDisplay
            messages={messages}
            isLoading={isLoading}
            onClearHistory={clearConversationHistory}
          />
        </div>

        {/* Input Area */}
        <div className="chat-input-area">
          <ChatInput
            onSendMessage={handleSendMessage}
            onVoiceInput={handleVoiceInput}
            isLoading={isLoading}
          />
        </div>

        {/* Resize Handles */}
        {['top', 'right', 'bottom', 'left', 'top-right', 'bottom-right', 'bottom-left', 'top-left'].map(direction => (
          <div
            key={`resize-${direction}`}
            className={`resize-handle resize-${direction}`}
            onMouseDown={(e) => handleResizeStart(direction, e)}
            onTouchStart={(e) => handleResizeStart(direction, e)}
          />
        ))}
      </div>

      <MessageHover text={sentText} visible={showHover} />
    </div>
  );
};

export default ChatBox;